import { memo, useState } from "react";
import { Container, Grid2, TextField, Typography, Box, Link } from "@mui/material";
import ArrowButton from "@/ui-kit/ArrowButton";
import { REQUEST_PASSWORD_RESET } from "@auth/graphql/mutations/requestPasswordReset";
import { ApolloError, useMutation } from "@apollo/client";
import { validateEmail } from "@/utils/validateCredentials";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification } from "@/utils/errorHandling";
import { triggerNotification } from "@/app/store/slices/notification";
import { RouteTo } from "@/app/routing/RouteMap";

interface FormErrors {
    email?: string;
    submit?: string;
}

const ForgotPasswordPage = memo(() => {
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [requestPasswordResetMutation, { loading }] = useMutation(REQUEST_PASSWORD_RESET);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        
        if (!email) {
            newErrors.email = "E-Mail-Adresse ist erforderlich";
        } else {
            const emailError = validateEmail(email);
            if (emailError) {
                newErrors.email = emailError;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        requestPasswordResetMutation({
            variables: { email },
            onError: (error: ApolloError) => {
                handleErrorWithNotification(
                    error, 
                    dispatch, 
                    "Fehler beim Senden der E-Mail", 
                    "Es gab ein Problem beim Senden der Passwort-Zurücksetzungs-E-Mail. Bitte versuchen Sie es später erneut."
                );
            },
            onCompleted: () => {
                setIsSubmitted(true);
                dispatch(triggerNotification({
                    content: "Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen eine E-Mail zum Zurücksetzen des Passworts gesendet.",
                    type: "success",
                    autoHideDuration: 5000
                }));
            },
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handleSubmit();
        }
    };

    if (isSubmitted) {
        return (
            <Container maxWidth="xs">
                <Grid2 container size={12} spacing={3} justifyContent="center">
                    <Grid2 mb={1}>
                        <Typography variant="h2">E-Mail gesendet</Typography>
                    </Grid2>

                    <Grid2 size={12}>
                        <Typography variant="body1" textAlign="center">
                            Falls ein Konto mit der E-Mail-Adresse <strong>{email}</strong> existiert, 
                            haben wir Ihnen eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts gesendet.
                        </Typography>
                    </Grid2>

                    <Grid2 size={12}>
                        <Typography variant="body2" textAlign="center" color="text.secondary">
                            Überprüfen Sie auch Ihren Spam-Ordner. Der Link ist 1 Stunde gültig.
                        </Typography>
                    </Grid2>

                    <Grid2 container spacing={2} justifyContent="center">
                        <Grid2>
                            <Link href={RouteTo.LOGIN} variant="nospace">Zurück zur Anmeldung</Link>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Container>
        );
    }

    return (
        <Container maxWidth="xs">
            <Grid2 container size={12} spacing={3} justifyContent="center">
                <Grid2 mb={4} mt={4}>
                    <Typography variant="h2">Passwort vergessen?</Typography>
                </Grid2>

                <Grid2 size={12}>
                    <Typography variant="body1" textAlign="center" color="text.secondary">
                        Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
                    </Typography>
                </Grid2>

                <Grid2 container size={12} spacing={2}>
                    <Grid2 size={12}>
                        <TextField
                            fullWidth
                            placeholder="E-Mail-Adresse"
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setErrors((prev) => ({ ...prev, email: undefined, submit: undefined }));
                            }}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={loading}
                            onKeyDown={handleKeyDown}
                            autoFocus
                        />
                    </Grid2>

                    {errors.submit && (
                        <Grid2 size={12}>
                            <Box sx={{ color: "semantic.notification.error", mt: 1 }}>{errors.submit}</Box>
                        </Grid2>
                    )}
                </Grid2>

                <Grid2 container spacing={2} justifyContent="center" width="100%">
                    <Grid2>
                        <Link href={RouteTo.LOGIN} variant="nospace">Zurück zur Anmeldung</Link>
                    </Grid2>
                </Grid2>

                <Grid2>
                    <ArrowButton onClick={handleSubmit} disabled={loading || !email} sx={{ mt: 2 }}>
                        {loading ? "Sende E-Mail..." : "E-Mail senden"}
                    </ArrowButton>
                </Grid2>
            </Grid2>
        </Container>
    );
});

export default ForgotPasswordPage; 